import { MailOutlined, MobileOutlined, PhoneOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  ClipboardCopy,
  Div,
  DropdownButton,
  EmailLink,
  Flex,
  FlexCenter,
  Icon,
  SapId,
} from 'ui';
import { partnerTypesWithContacts } from 'utils/app-configs';
import { toSnakeCase } from 'utils/helpers';

const PartnerType = styled(Div)`
  line-height: 12px;
`;

const Title = styled(CardTitle)`
  line-height: 20px;
  font-size: 18px;
  white-space: normal;
  overflow-wrap: anywhere;
`;

const PartnerIcon = styled(FlexCenter)`
  > i {
    color: ${p => p.theme.colors.theme};
    font-size: 20px;
  }
`;

const TelIcon = styled(PhoneOutlined)`
  margin-right: 5px;
`;

const MobileIcon = styled(MobileOutlined)`
  margin-right: 5px;
`;

const MailIcon = styled(MailOutlined)`
  margin-right: 5px;
`;

const Content = styled(Div)`
  font-size: 19px;
  div{
    display: flex;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const TypeAndTitle = styled(Flex)`
  overflow-x: hidden;
  flex: 1;
`;

const StyledDropdownButton = styled(DropdownButton)`
  display: flex;
  align-items: center;
`;

// eslint-disable-next-line complexity
const PartnerCard = ({
  partner,
  type,
  changePartner,
  removePartner,
  canEdit,
  cardsPerLine,
  position,
  icon,
  intl,
}) => {
  const addComma = address => address ? `${address}, ` : '';

  if (!type) {
    return <span />;
  }

  const options = [
    {
      text: intl.formatMessage({ id: 'common.CHANGE' }),
      onClick: () =>
        changePartner({
          type: partner.partnerFunction,
          name: toSnakeCase(type.name).toUpperCase(),
          id: partner.id,
          isNew: false,
        }),
      disabled: !type.canEdit,
    },
    {
      text: intl.formatMessage({ id: 'common.REMOVE' }),
      onClick: () => removePartner({ id: partner.id }),
      disabled: !type.clearable,
    },
  ];

  const renderContent = () => {
    if (partnerTypesWithContacts.includes(partner.partnerFunction.toUpperCase())) {
      return (
        <Content>
          <SapId originalId={partner.number}>
            {sapId => <>{intl.formatMessage({ id: 'partners.SAP_NR' })}{sapId}</>}
          </SapId>
          {partner.phoneNumber &&
            // Must be a div otherwise all the data will be on one line
            <div>
              <TelIcon />
              <ClipboardCopy prefixWidth='25px'>{partner.phoneNumber}</ClipboardCopy>
            </div>
          }
          {partner.mobileNumber &&
            <div>
              <MobileIcon />
              <ClipboardCopy prefixWidth='25px'>{partner.mobileNumber}</ClipboardCopy>
            </div>
          }
          {partner.email &&
            <div>
              <MailIcon />
              <ClipboardCopy onlyCopyThroughIcon={true} prefixWidth='25px'>
                <EmailLink href={`mailto:${partner.email}`}>{partner.email}</EmailLink>
              </ClipboardCopy>
            </div>
          }
        </Content>
      );
    }

    return (
      <Content>
        <SapId originalId={partner.number}>
          {sapId => <Div>{intl.formatMessage({ id: 'partners.SAP_NR' })}{sapId}</Div>}
        </SapId>
        {(partner.street || partner.houseNumber) &&
          <Div>{addComma(`${partner.street || ''} ${partner.houseNumber || ''}`)}</Div>
        }
        {(partner.postalCode || partner.city) &&
          <Div>{addComma(`${partner.postalCode || ''} ${partner.city || ''}`)}</Div>
        }
        {(partner.regionName || partner.countryName) &&
          <Div>{`${partner.regionName || ''} ${partner.countryName || ''}`}</Div>
        }
      </Content>
    );
  };

  return (
    <Card gutter={15} position={position} cardsPerLine={cardsPerLine} data-test-id='partner-card'>
      <CardHeader skipBorder={true} paddingTop={15}>
        <Flex width='100%'>
          <PartnerIcon>
            {partnerTypesWithContacts.includes(type.type.toUpperCase()) && <Icon type='user' />}
            {!partnerTypesWithContacts.includes(type.type.toUpperCase()) &&
              <Icon type={icon || 'industry'} />
            }
          </PartnerIcon>
          <TypeAndTitle fdc marginLeft={5}>
            <PartnerType fontWeightBolder>{toSnakeCase(type.name).toUpperCase()}</PartnerType>
            <Title>{partner.name}</Title>
          </TypeAndTitle>
          <FlexCenter>
            {(type.canEdit || type.clearable) && canEdit &&
              <StyledDropdownButton
                iconClass='fa fa-ellipsis-h'
                asSeperate={false}
                subItems={options}
              />
            }
          </FlexCenter>
        </Flex>
      </CardHeader>
      <CardBody paddingTop={5} paddingBottom={15}>
        {renderContent()}
      </CardBody>
    </Card>
  );
};

PartnerCard.propTypes = {
  partner: PropTypes.object,
  type: PropTypes.object,
  changePartner: PropTypes.func,
  removePartner: PropTypes.func,
  canEdit: PropTypes.bool,
  cardsPerLine: PropTypes.number,
  position: PropTypes.number,
  icon: PropTypes.string,
  intl: PropTypes.object,
};

export default injectIntl(PartnerCard);
